import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Menu, X, Clock, MapPin, Calendar, Building2, Users, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const navLinks = [
  { to: '/', label: 'Home', icon: Clock },
  { to: '/timetables', label: 'Timetables', icon: Calendar },
  { to: '/prayers-by-date', label: 'Prayer Times', icon: Clock },
  { to: '/mosques', label: 'Mosques', icon: Building2 },
  { to: '/jummah', label: "Jumu'ah", icon: Users },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { admin, logoutAdmin } = useAuth();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'navbar-blur shadow-lg' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center glow-emerald group-hover:scale-110 transition-transform">
                <Moon className="w-5 h-5 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-gold-500 border-2 border-navy-900"></div>
            </div>
            <div>
              <span className="font-bold text-white text-lg leading-tight block">Oldham</span>
              <span className="text-xs text-emerald-400 leading-tight block tracking-wider">PRAYER TIMES</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(({ to, label, icon: Icon }) => {
              const isActive = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'text-gray-300 hover:text-emerald-400 hover:bg-emerald-500/10'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </Link>
              );
            })}

            {admin ? (
              <div className="flex items-center gap-2 ml-4">
                <Link
                  to="/admin/dashboard"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gold-400 hover:bg-gold-500/10 transition-all"
                >
                  <LogIn className="w-4 h-4" />
                  Dashboard
                </Link>
                <button
                  onClick={logoutAdmin}
                  className="text-xs text-gray-400 hover:text-red-400 transition-colors px-2 py-1"
                >
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/admin/login"
                className={`flex items-center gap-2 ml-4 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/admin/login'
                    ? 'bg-gold-500/20 text-gold-400 border border-gold-500/30'
                    : 'text-gray-300 hover:text-gold-400 border border-transparent hover:border-gold-500/30 hover:bg-gold-500/10'
                }`}
              >
                <LogIn className="w-4 h-4" />
                Admin
              </Link>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-all"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden navbar-blur border-t border-emerald-500/20"
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map(({ to, label, icon: Icon }) => {
                const isActive = location.pathname === to;
                return (
                  <Link
                    key={to}
                    to={to}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'text-gray-300 hover:text-emerald-400 hover:bg-emerald-500/10'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </Link>
                );
              })}

              {admin ? (
                <>
                  <Link
                    to="/admin/dashboard"
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gold-400 hover:bg-gold-500/10 transition-all"
                  >
                    <LogIn className="w-4 h-4" />
                    Admin Dashboard
                  </Link>
                  <button
                    onClick={logoutAdmin}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/admin/login"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gold-400 hover:bg-gold-500/10 transition-all"
                >
                  <LogIn className="w-4 h-4" />
                  Admin Login
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
